import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, X, Euro, Loader2, Crown, Zap, Rocket, Clock } from "lucide-react";

const STATUS_CONFIG = {
  active: { label: "Activo", color: "bg-green-500/20 text-green-400" },
  lifetime: { label: "Vitalicio ♾️", color: "bg-amber-500/20 text-amber-400" },
  trial: { label: "Prueba Gratuita", color: "bg-blue-500/20 text-blue-400" },
  past_due: { label: "Pago Pendiente", color: "bg-yellow-500/20 text-yellow-400" },
  suspended: { label: "Suspendido", color: "bg-red-500/20 text-red-400" },
  cancelled: { label: "Cancelado", color: "bg-gray-500/20 text-gray-400" }
};

const PLAN_ICONS = { basic: Zap, pro: Rocket, enterprise: Crown };
const PLAN_COLORS = {
  basic: "border-blue-500/30 bg-blue-500/5",
  pro: "border-purple-500/50 bg-purple-500/5 ring-2 ring-purple-500/20",
  enterprise: "border-amber-500/30 bg-amber-500/5",
};

export const SubscriptionTab = ({ authAxios, restaurant, checkingPayment, handleSubscribe, plans, plansCurrencySymbol = "€", fetchRestaurant }) => {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [billing, setBilling] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    authAxiosRef.current.get("/subscription/billing").then(r => { setBilling(r.data); setLoadingBilling(false); }).catch(() => setLoadingBilling(false));
  }, []);

  const cancelSub = async () => {
    if (!window.confirm("Estas seguro de cancelar tu suscripcion?")) return;
    setCancelling(true);
    try {
      await authAxios.post("/subscription/cancel");
      toast.success("Suscripcion cancelada");
      fetchRestaurant();
      authAxios.get("/subscription/billing").then(r => setBilling(r.data));
    } catch { toast.error("Error al cancelar"); }
    setCancelling(false);
  };

  const reactivate = async () => {
    try {
      await authAxios.post("/subscription/reactivate");
      toast.success("Suscripcion reactivada");
      fetchRestaurant();
      authAxios.get("/subscription/billing").then(r => setBilling(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const subStatus = restaurant?.subscription_status || "trial";
  const isLifetime = subStatus === "lifetime" || restaurant?.subscription_lifetime;
  const statusCfg = STATUS_CONFIG[subStatus] || STATUS_CONFIG.trial;

  return (
    <div className="space-y-6">
      {checkingPayment && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="flex items-center gap-4 py-4">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
            <p>Verificando pago...</p>
          </CardContent>
        </Card>
      )}

      {/* Trial banner */}
      {subStatus === "trial" && billing?.trial_days_left != null && (
        <Card className="border-blue-500/50 bg-blue-500/5" data-testid="trial-banner">
          <CardContent className="flex items-center gap-4 py-4">
            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0"><Clock className="w-5 h-5 text-blue-400" /></div>
            <div className="flex-1">
              <p className="font-semibold text-blue-400">Período de prueba</p>
              <p className="text-sm text-muted-foreground">
                Te quedan <strong>{billing.trial_days_left} día(s)</strong> de prueba gratuita. Suscríbete para no perder acceso.
              </p>
            </div>
            <div className="text-3xl font-bold text-blue-400">{billing.trial_days_left}d</div>
          </CardContent>
        </Card>
      )}

      {subStatus === "past_due" && (
        <Card className="border-yellow-500/50 bg-yellow-500/5" data-testid="past-due-warning">
          <CardContent className="flex items-center gap-4 py-4">
            <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center shrink-0"><Euro className="w-5 h-5 text-yellow-400" /></div>
            <div>
              <p className="font-semibold text-yellow-400">Pago pendiente</p>
              <p className="text-sm text-muted-foreground">
                Tu pago ha fallado.{billing?.days_until_suspension != null && ` Tienes ${billing.days_until_suspension} dia(s) para actualizar tu metodo de pago.`}
                {" "}Despues de 5 dias tu cuenta sera suspendida.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      {subStatus === "suspended" && (
        <Card className="border-red-500/50 bg-red-500/5" data-testid="suspended-warning">
          <CardContent className="flex items-center gap-4 py-4">
            <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center shrink-0"><X className="w-5 h-5 text-red-400" /></div>
            <div>
              <p className="font-semibold text-red-400">Cuenta suspendida</p>
              <p className="text-sm text-muted-foreground">Tu cuenta ha sido suspendida por falta de pago. Realiza un pago para reactivar.</p>
            </div>
            <Button onClick={reactivate} className="glow-orange ml-auto shrink-0" data-testid="reactivate-btn">Reactivar</Button>
          </CardContent>
        </Card>
      )}

      {isLifetime && (
        <Card className="border-amber-500/50 bg-amber-500/5" data-testid="lifetime-banner">
          <CardContent className="flex items-center gap-4 py-4">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0"><Crown className="w-5 h-5 text-amber-400" /></div>
            <div className="flex-1">
              <p className="font-semibold text-amber-400">Assinatura Vitalícia ♾️</p>
              <p className="text-sm text-muted-foreground">Acesso total e permanente a todos os recursos Premium. Sem renovações, sem pagamentos.</p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="uppercase">Tu Plan Actual</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Badge className={statusCfg.color} data-testid="subscription-status">{statusCfg.label}</Badge>
              <span className="font-semibold text-lg capitalize">{isLifetime ? "Premium" : (billing?.plan_name || restaurant?.subscription_plan || "trial")}</span>
              {!isLifetime && billing?.plan_price > 0 && <span className="text-muted-foreground">{billing.plan_price}{plansCurrencySymbol}/mes</span>}
            </div>
            {!isLifetime && subStatus === "active" && (
              <Button variant="ghost" size="sm" onClick={cancelSub} disabled={cancelling} className="text-red-400 hover:text-red-300" data-testid="cancel-subscription-btn">
                {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancelar suscripcion"}
              </Button>
            )}
            {!isLifetime && (subStatus === "cancelled" || subStatus === "past_due") && (
              <Button onClick={reactivate} variant="outline" size="sm" data-testid="reactivate-btn-alt">Reactivar</Button>
            )}
          </div>
          {!isLifetime && billing?.next_billing_date && (
            <p className="text-sm text-muted-foreground">Proxima facturacion: {new Date(billing.next_billing_date).toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" })}</p>
          )}
        </CardContent>
      </Card>

      {!isLifetime && <div className="grid md:grid-cols-3 gap-6">
        {Object.entries(plans).map(([key, plan]) => {
          const Icon = PLAN_ICONS[key] || Zap;
          const colorClass = PLAN_COLORS[key] || "";
          const isCurrent = restaurant?.subscription_plan === key;
          const isPopular = key === "pro";
          return (
            <Card key={key} className={`relative overflow-hidden transition-all ${isCurrent ? "border-primary ring-2 ring-primary/30" : colorClass}`}>
              {isPopular && (
                <div className="absolute top-0 right-0 bg-purple-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg uppercase">Más popular</div>
              )}
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="w-5 h-5 text-muted-foreground" />
                  <CardTitle className="text-xl uppercase">{plan.name}</CardTitle>
                </div>
                <CardDescription>
                  <span className="text-4xl font-bold text-foreground">{plansCurrencySymbol}{plan.price}</span>
                  <span className="text-muted-foreground">/mes</span>
                </CardDescription>
                {plan.max_estafetas && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {plan.max_estafetas === -1 ? "Estafetas ilimitados" : `Hasta ${plan.max_estafetas} estafeta${plan.max_estafetas > 1 ? "s" : ""}`}
                    {plan.allow_video ? " | Videos en menú" : " | Solo imágenes"}
                  </p>
                )}
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-6">
                  {(plan.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />{f}</li>
                  ))}
                </ul>
                <Button
                  className={`w-full ${isPopular ? "bg-purple-600 hover:bg-purple-700" : ""}`}
                  variant={isCurrent ? "secondary" : "default"}
                  onClick={() => handleSubscribe(key)}
                  disabled={isCurrent}
                  data-testid={`subscribe-${key}-btn`}
                >
                  {isCurrent ? "Plan actual" : (subStatus === "suspended" ? "Reactivar con este plan" : "Suscribirse")}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>}

      {billing?.transactions?.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader><CardTitle className="uppercase">Historial de Pagos</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="grid grid-cols-5 gap-4 text-xs font-semibold text-muted-foreground uppercase px-2 pb-2 border-b border-border/30">
                <span>Fecha</span><span>Plan</span><span>Monto</span><span>Estado</span><span>ID Sesion</span>
              </div>
              {billing.transactions.map(tx => (
                <div key={tx.id} className="grid grid-cols-5 gap-4 items-center py-2 px-2 text-sm border-b border-border/10" data-testid="payment-history-row">
                  <span>{new Date(tx.created_at).toLocaleDateString("es-ES")}</span>
                  <span className="capitalize">{tx.plan}</span>
                  <span className="font-medium">{plansCurrencySymbol}{tx.amount}</span>
                  <Badge className={tx.payment_status === "paid" ? "bg-green-500/20 text-green-400" : tx.payment_status === "initiated" ? "bg-yellow-500/20 text-yellow-400" : "bg-gray-500/20 text-gray-400"}>
                    {tx.payment_status === "paid" ? "Pagado" : tx.payment_status === "initiated" ? "Pendiente" : tx.payment_status}
                  </Badge>
                  <span className="font-mono text-xs text-muted-foreground truncate">{tx.session_id?.slice(0, 16)}...</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {loadingBilling && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}
    </div>
  );
};
